import { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  signInWithPopup,
  OAuthProvider,
} from "firebase/auth";
import { auth, db } from "@/firebase/client";
import { toastError, toastSuccess, toastInfo } from "@/components/ui/toast";
import { useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  increment,
} from "firebase/firestore";
import { UserData, hasRole } from "@/lib/types";
import { getReferralData, clearReferralData } from "@/lib/referral-utils";
import { setSentryUser, clearSentryUser } from "@/lib/sentry";

type AuthContextType = {
  user: User | null;
  userData: UserData | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any | null }>;
  signUp: (
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ) => Promise<{ error: any | null; existingUser: boolean }>;
  signInWithOAuth: (provider: "google" | "apple") => Promise<{ error: any | null }>;
  signOut: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  impersonateUser: (userId: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  isImpersonating: boolean;
  originalAdminData: UserData | null;
  currentUserId: string | null;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalAdminData, setOriginalAdminData] = useState<UserData | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const navigate = useNavigate();

  // Create or update user data (with token initialization and referral attribution)
  const initializeUserData = useCallback(
    async (user: User, isNewUser: boolean = false) => {
      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists() || isNewUser) {
          // New user - initialize with 5 free tokens
          const newUserData: UserData = {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            planTier: "free",
            remainingGenerations: 5, // 5 free tokens for new users
            projectsCount: 0,
            maxProjects: 10, // Default limit for free tier
            createdAt: new Date(),
            updatedAt: new Date(),
            isAdmin: false,
            hasCompletedFirstPurchase: false,
          };

          // Check for referral attribution
          const referralData = getReferralData();
          if (referralData && referralData.code) {
            console.log("Found referral data for new user:", referralData.code);

            // Find the affiliate by code
            const affiliateData = await findAffiliateByCode(referralData.code);
            if (affiliateData) {
              newUserData.referredByAffiliateId = affiliateData.id;
              newUserData.referredByCode = referralData.code;

              // Create referral record and update affiliate stats
              await createReferralRecord(affiliateData, user, referralData);

              console.log("Referral attribution successful for affiliate:", affiliateData.id);
            }

            // Clear the referral cookie after attribution
            clearReferralData();
          }

          await setDoc(
            userRef,
            {
              ...newUserData,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );

          setUserData(newUserData);
          return newUserData;
        } else {
          // Existing user - just fetch their data
          const userData = userSnap.data() as UserData;
          setUserData(userData);
          return userData;
        }
      } catch (error) {
        console.error("Error initializing user data:", error);
        return null;
      }
    },
    [setUserData]
  );

  // Helper function to find affiliate by code
  const findAffiliateByCode = async (code: string) => {
    try {
      const affiliatesRef = collection(db, "affiliates");
      const q = query(
        affiliatesRef,
        where("affiliateCode", "==", code.toUpperCase()),
        where("status", "==", "active")
      );
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      return null;
    } catch (error) {
      console.error("Error finding affiliate:", error);
      return null;
    }
  };

  // Helper function to create referral record and update affiliate stats
  const createReferralRecord = async (
    affiliate: { id: string; affiliateCode?: string },
    user: User,
    referralData: {
      code: string;
      landingPage: string;
      utmSource?: string;
      utmMedium?: string;
      utmCampaign?: string;
    }
  ) => {
    try {
      // Generate a simple ID for the referral
      const referralId = `ref_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Create the referral record
      const referralRef = doc(db, "referrals", referralId);
      await setDoc(referralRef, {
        id: referralId,
        affiliateId: affiliate.id,
        affiliateCode: affiliate.affiliateCode || referralData.code,
        referredUserId: user.uid,
        referredUserEmail: user.email || "",
        utmSource: referralData.utmSource || null,
        utmMedium: referralData.utmMedium || null,
        utmCampaign: referralData.utmCampaign || null,
        landingPage: referralData.landingPage || null,
        status: "signed_up",
        clickedAt: serverTimestamp(),
        signedUpAt: serverTimestamp(),
      });

      // Update affiliate's totalReferrals count
      const affiliateRef = doc(db, "affiliates", affiliate.id);
      await updateDoc(affiliateRef, {
        totalReferrals: increment(1),
        updatedAt: serverTimestamp(),
      });

      console.log("Created referral record:", referralId);
    } catch (error) {
      console.error("Error creating referral record:", error);
      // Don't throw - referral tracking failure shouldn't break signup
    }
  };

  const fetchUserData = useCallback(
    async (user: User) => {
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          setUserData(userDoc.data() as UserData);
        } else {
          // Create new user data with default values
          await initializeUserData(user, true);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        toastError({
          title: "Data Fetch Failed",
          description: "Failed to fetch user data. Please try again.",
        });
      }
    },
    [initializeUserData]
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async user => {
      setUser(user);
      if (user) {
        // Set Sentry user context for error tracking
        setSentryUser(user.uid, user.email || undefined);

        await fetchUserData(user);
        // Only set currentUserId if we're not currently impersonating
        setCurrentUserId(prevId => (isImpersonating ? prevId : user.uid));
      } else {
        // Clear Sentry user context on logout
        clearSentryUser();

        setUserData(null);
        // Reset impersonation state when user signs out
        setIsImpersonating(false);
        setOriginalAdminData(null);
        setCurrentUserId(null);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [fetchUserData, navigate, isImpersonating]);

  const refreshUserData = async () => {
    if (user) {
      await fetchUserData(user);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);

      toastSuccess({
        title: "Signed In",
        description: "Welcome back to StickersBy AI!",
      });

      navigate("/dashboard");
      return { error: null };
    } catch (error: any) {
      toastError({
        title: "Sign In Failed",
        description: error.message,
      });
      return { error };
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      // Instead of checking if email exists first, try to create account directly
      // Firebase will handle the "email already exists" error
      const { user } = await createUserWithEmailAndPassword(auth, email, password);

      // Initialize user data with 5 free tokens
      await initializeUserData(user, true);

      toastSuccess({
        title: "Account Created",
        description: "Welcome to StickersBy AI!",
      });

      navigate("/dashboard");
      return { error: null, existingUser: false };
    } catch (error: any) {
      // Handle specific Firebase error codes
      if (error.code === "auth/email-already-in-use") {
        toastError({
          title: "Sign Up Failed",
          description: "This email is already in use. Please try signing in instead.",
        });
        return { error, existingUser: true };
      }

      toastError({
        title: "Sign Up Failed",
        description: error.message,
      });
      return { error, existingUser: false };
    }
  };

  const signInWithOAuth = async (provider: "google" | "apple") => {
    try {
      let authProvider: GoogleAuthProvider | OAuthProvider;
      if (provider === "google") {
        authProvider = new GoogleAuthProvider();
        authProvider.setCustomParameters({
          prompt: "select_account",
        });
      } else {
        authProvider = new OAuthProvider("apple.com");
      }

      // Use popup instead of redirect - handles auth state immediately
      const result = await signInWithPopup(auth, authProvider);

      // Check if this is a new user
      const isNewUser = result.user.metadata.creationTime === result.user.metadata.lastSignInTime;

      // Initialize user data (with tokens if new user)
      await initializeUserData(result.user, isNewUser);

      toastSuccess({
        title: "Signed In",
        description: "Welcome to StickersBy AI!",
      });

      // Check for stored redirect URL (e.g., from affiliate signup flow)
      const storedRedirect = sessionStorage.getItem("auth-redirect-to");
      if (storedRedirect) {
        sessionStorage.removeItem("auth-redirect-to");
        navigate(storedRedirect);
      } else {
        navigate("/dashboard");
      }
      return { error: null };
    } catch (error: any) {
      console.error("OAuth sign-in error:", error);

      if (error.code === "auth/popup-blocked") {
        toastError({
          title: "Popup Blocked",
          description: "Please allow popups for this site in your browser settings.",
        });
      } else if (error.code === "auth/popup-closed-by-user") {
        // User closed the popup, don't show an error
      } else {
        toastError({
          title: `${provider} Sign In Failed`,
          description: error.message,
        });
      }
      return { error };
    }
  };

  const logout = async () => {
    await signOut(auth);
    toastInfo({
      title: "Signed Out",
      description: "You have been signed out successfully",
    });
    navigate("/");
  };

  // Impersonation functions
  const impersonateUser = async (userId: string) => {
    try {
      // Check if current user is an admin
      if (!hasRole(userData, "admin")) {
        toastError({
          title: "Impersonation Failed",
          description: "Only admin users can impersonate other users",
        });
        return;
      }

      // Save current admin user data
      setOriginalAdminData(userData);

      // Fetch the user data of the user to impersonate
      const userDoc = await getDoc(doc(db, "users", userId));

      if (!userDoc.exists()) {
        toastError({
          title: "Impersonation Failed",
          description: "User not found",
        });
        return;
      }

      const impersonatedUserData = userDoc.data() as UserData;

      // Set the impersonated user data without changing the actual auth state
      setUserData(impersonatedUserData);
      setIsImpersonating(true);
      setCurrentUserId(userId);

      toastSuccess({
        title: "Impersonation Active",
        description: `You are now viewing the app as ${impersonatedUserData.email || "the user"}`,
      });

      // Navigate to dashboard to see the impersonated user's view
      navigate("/dashboard");
    } catch (error: any) {
      console.error("Impersonation error:", error);
      toastError({
        title: "Impersonation Failed",
        description: error.message || "Failed to impersonate user",
      });
    }
  };

  const stopImpersonation = async () => {
    if (!isImpersonating || !originalAdminData) {
      return;
    }

    // Restore original admin data
    setUserData(originalAdminData);
    setIsImpersonating(false);
    setOriginalAdminData(null);
    setCurrentUserId(null);

    toastInfo({
      title: "Impersonation Ended",
      description: "You have returned to your admin account",
    });

    // Navigate to admin dashboard or similar
    navigate("/dashboard");
  };

  const value = {
    user,
    userData,
    isLoading,
    signIn,
    signUp,
    signInWithOAuth,
    signOut: logout,
    refreshUserData,
    impersonateUser,
    stopImpersonation,
    isImpersonating,
    originalAdminData,
    currentUserId,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
